import {DomainStore} from 'shared/store';
import {observable, action, computed} from 'mobx';
import {setupAutosaveDraft, successAlert} from 'shared/tools';
import {auth, endpoints, types, t} from 'shared/core';
import {Goal, ProgressUpdate} from 'stores/performance_goals';
import _ from 'lodash';

class ViewGoalContainerState {
  store = new DomainStore();

  @observable goal;
  @observable progressUpdate;
  @observable errors = {};
  @observable deleteProgressUpdateModalOpen = false;
  @observable editingProgressUpdate;
  @observable deleteGoalModalOpen = false;
  @observable editGoalModalOpen = false;
  @observable editingGoal;

  receiveProps({match, history}) {
    this.match = match;
    this.history = history;
  }

  @action async load() {
    await this.store._compose([
      endpoints.PERFORMANCE_GOALS.GOAL.with(this.match.params.goalId)
    ]);

    this.goal = new Goal(this.store._getSingle(
      types.PERFORMANCE_GOALS.GOAL
    ));
    await this.initializeProgressUpdate();
  }

  @action async initializeProgressUpdate() {
    this.progressUpdate = new ProgressUpdate({
      employeeId: auth.employee.id,
      goalId: this.goal.id,
      completeness: this.goal.completeness,
      comment: ''
    });
    await setupAutosaveDraft(
      this.progressUpdate,
      {id: this.goal.id, type: types.PERFORMANCE_GOALS.PROGRESS_UPDATE}
    );
  }

  @action updateComment(comment) {
    this.progressUpdate.comment = comment;
    this.progressUpdate.autosaver.autosave();
  }

  @action updateCompleteness(completeness) {
    this.progressUpdate.completeness = completeness;
    this.progressUpdate.autosaver.autosave();
  }

  @action async saveProgressUpdate() {
    const {model, errors} = await this.store.post(
      this.goal.link('createProgressUpdate'),
      types.PERFORMANCE_GOALS.PROGRESS_UPDATE,
      this.progressUpdate
    );
    this.errors = errors;

    if (model) {
      this.addProgressUpdateToGoal(model);
      await this.initializeProgressUpdate();
    }
  }

  @action addProgressUpdateToGoal(model) {
    const progressUpdate = new ProgressUpdate(model);
    this.goal.progressUpdates.push(progressUpdate);
    if (progressUpdate.showProgressIndicator) {
      this.goal.merge({completeness: progressUpdate.completeness});
    }
  }

  @action openDeleteProgressUpdateModal(update) {
    this.deleteProgressUpdateModalOpen = true;
    this.editingProgressUpdate = update;
  }

  @action closeDeleteProgressUpdateModal() {
    this.deleteProgressUpdateModalOpen = false;
    this.editingProgressUpdate = null;
  }

  @action async deleteProgressUpdate() {
    const progressUpdate = this.editingProgressUpdate;
    await this.store.destroy(this.editingProgressUpdate);
    this.closeDeleteProgressUpdateModal();
    successAlert(t('goals.Progress deleted'));
    this.goal.progressUpdates.remove(progressUpdate);
  }

  @action openDeleteGoalModal() {
    this.deleteGoalModalOpen = true;
  }

  @action closeDeleteGoalModal() {
    this.deleteGoalModalOpen = false;
  }

  @action async deleteGoal() {
    await this.store.destroy(this.goal);
    this.closeDeleteProgressUpdateModal();
    successAlert(t('goals.Goal deleted'));
    this.history.push(this.backPath);
  }

  @action async openEditGoalModal() {
    this.editingGoal = new Goal(this.goal);
    this.editGoalModalOpen = true;
  }

  @action closeEditGoalModal() {
    this.editGoalModalOpen = false;
  }

  @action async saveGoal() {
    const {model, errors} = await this.store.patch(this.editingGoal);
    this.errors = errors;

    if (model) {
      this.goal.update(model);
      this.closeEditGoalModal();
      successAlert(t('goals.Goal updated'));
    }
  }

  @action updateGoal(updatedAttributes) {
    this.editingGoal.merge(updatedAttributes);
  }

  @computed get changesMade() {
    const progressChanged = this.progressUpdate.completeness !== this.goal.completeness;
    const commentAdded = !_.isEmpty(this.progressUpdate.comment);
    return progressChanged || commentAdded;
  }

  @computed get canEdit() {
    return this.goal.canPatch;
  }

  @computed get backTitle() {
    if (this.goal.employee.id === auth.employee.id) {
      return t('components.navigation.Back');
    } else {
      return t('goals.EMPLOYEES_GOALS', {employeeName: this.goal.employee.name});
    }
  }

  @computed get backPath() {
    const filters = window.location.search;
    if (this.goal.employee.id === auth.employee.id) {
      return `/my-goals${filters}`;
    } else {
      return `/employees/${this.goal.employee.id}${filters}`;
    }
  }
}

export default ViewGoalContainerState;
